import { SwimlaneSummary } from '@flowx/designer/modules/core/dictionary/swimlane.dictionary'
import { BoundaryEventType } from '@flowx/designer/shared/entities/boundary-event/boundary-event.common'
import { NodeType } from '@flowx/designer/shared/entities/node.types'

import {
  checkPaletteDropValidity,
  findSwimlaneAt,
  snapToNodeBorder,
  toAbsolutePosition,
  toSwimlaneRelative,
} from './bpmn-graph.mapper'

import { CanvasData, NodeDefinitionRender } from '../../bpmn-canvas.types'
import { Point } from '../../dictionary/geometry.types'

type NodeDimensions = (type: NodeType | BoundaryEventType) => { width: number; height: number }

/** Drop-validity messages, surfaced by the host as a toast. */
export const LANE_DROP_ERRORS = {
  outsideSwimlane: 'Node is not in swimlane',
  overlapping: 'Node overlaps another node',
} as const

export type LaneNodeDropPlan =
  | { valid: true; swimlane: SwimlaneSummary; position: Point; error: null }
  | { valid: false; swimlane: SwimlaneSummary | null; position: null; error: string }

/**
 * Drop validity for a node landing on empty lane surface: the drop point is the
 * node's center, the resulting position is
 * lane-relative and top-left based.
 */
export function planLaneNodeDrop(
  absoluteCenter: Point,
  type: NodeType | BoundaryEventType,
  data: CanvasData,
  nodeDimensions: NodeDimensions
): LaneNodeDropPlan {
  const swimlane = findSwimlaneAt(absoluteCenter, data.swimlanes)
  if (!swimlane) {
    return { valid: false, swimlane: null, position: null, error: LANE_DROP_ERRORS.outsideSwimlane }
  }
  const { width, height } = nodeDimensions(type)
  const { isInSwimlane, notOverlappingNodes } = checkPaletteDropValidity(
    absoluteCenter,
    { width, height },
    data,
    nodeDimensions
  )
  if (!isInSwimlane) {
    return { valid: false, swimlane, position: null, error: LANE_DROP_ERRORS.outsideSwimlane }
  }
  if (!notOverlappingNodes) {
    return { valid: false, swimlane, position: null, error: LANE_DROP_ERRORS.overlapping }
  }
  const relativePoint = toSwimlaneRelative(absoluteCenter, swimlane)

  return {
    valid: true,
    swimlane,
    position: { x: relativePoint.x - width / 2, y: relativePoint.y - height / 2 },
    error: null,
  }
}

/**
 * Boundary-event placement: the boundary event centred on the cursor is snapped
 * onto the closest border of the node under it. `relative` is what the
 * host persists (node-relative top-left), `absoluteCenter` is where the drag
 * preview draws it.
 */
export function planBoundaryDrop(
  flowPoint: Point,
  targetNode: NodeDefinitionRender,
  data: CanvasData,
  dimensions: { width: number; height: number }
): { relative: Point; absoluteCenter: Point } {
  const lane = data.swimlanes.find((swimlane) => swimlane.id === targetNode.swimlaneDefinitionId)
  const nodeAbsolute = toAbsolutePosition(
    { x: targetNode.layoutOptions?.x ?? 0, y: targetNode.layoutOptions?.y ?? 0 },
    lane
  )
  const relative = snapToNodeBorder(
    {
      x: flowPoint.x - nodeAbsolute.x - dimensions.width / 2,
      y: flowPoint.y - nodeAbsolute.y - dimensions.height / 2,
    },
    targetNode,
    dimensions
  )

  return {
    relative,
    absoluteCenter: {
      x: nodeAbsolute.x + relative.x + dimensions.width / 2,
      y: nodeAbsolute.y + relative.y + dimensions.height / 2,
    },
  }
}
